/**
 * ViroxaNest custom footer: AI Assistant button.
 *
 * When the footer's "AI Assistant backend URL" is set, the icon opens the ViroxaNest AI Assistant
 * (<vn-ai-chat>). Otherwise it opens Shopify Inbox: snippets/chat-drawer.liquid always renders a
 * <shopify-chat> element, and the Inbox app registers it as a working chat when Inbox is installed
 * with online store chat turned on.
 *
 * The button is a <button>, not a link, so it can never navigate. Earlier versions were an <a> whose
 * href was the Contact page: a tap before this script loaded, or a chat that took longer than 1.5s to
 * load (common on mobile), sent customers to the Contact form. Now the button only ever opens the chat
 * on the current page. If the chat can't load, it shows a short notice and stays on the page.
 */

const CHAT_READY_TIMEOUT = 10000;
const STATUS_HIDE_DELAY = 6000;

/** @returns {(HTMLElement & { show?: () => void }) | null} */
function getChat() {
  return document.querySelector('shopify-chat');
}

/** Waits for Inbox to register <shopify-chat>. Inbox loads after the page, later on slow mobile networks. */
function whenChatReady() {
  if (customElements.get('shopify-chat')) return Promise.resolve(true);
  return Promise.race([
    customElements.whenDefined('shopify-chat').then(() => true),
    new Promise((resolve) => setTimeout(() => resolve(false), CHAT_READY_TIMEOUT)),
  ]);
}

/**
 * The ViroxaNest AI Assistant (<vn-ai-chat>, snippets/vn-ai-chat.liquid). It is on the page only when
 * the footer's "AI Assistant backend URL" is set, and then it is the chat the icon opens.
 * @returns {Promise<(HTMLElement & { open: () => void }) | null>}
 */
async function getAiChat() {
  if (!document.querySelector('vn-ai-chat')) return null;
  const defined = await Promise.race([
    customElements.whenDefined('vn-ai-chat').then(() => true),
    new Promise((resolve) => setTimeout(() => resolve(false), CHAT_READY_TIMEOUT)),
  ]);
  const chat = /** @type {any} */ (document.querySelector('vn-ai-chat'));
  return defined && typeof chat?.open === 'function' ? chat : null;
}

/** Opens the one existing chat the same way its own launcher does. Never creates a second chat. */
function openChat() {
  const chat = getChat();
  if (!chat || !customElements.get('shopify-chat')) return false;
  if (chat.hasAttribute('open')) return true;
  if (typeof chat.show === 'function') chat.show();
  else chat.setAttribute('open', '');
  return true;
}

/** @param {HTMLElement} button @param {string} message */
function showStatus(button, message) {
  const id = button.getAttribute('aria-describedby');
  const status = id ? document.getElementById(id) : null;
  if (!status) return;
  status.textContent = message;
  // Sit just above the robot icon so the notice never covers it.
  const top = button.getBoundingClientRect().top;
  status.style.bottom = top > 80 ? `${Math.round(window.innerHeight - top + 12)}px` : '';
  status.hidden = false;
  clearTimeout(Number(status.dataset.timer));
  status.dataset.timer = String(setTimeout(() => (status.hidden = true), STATUS_HIDE_DELAY));
}

document.addEventListener('click', async (event) => {
  const button = /** @type {HTMLElement} */ (event.target).closest?.('[data-vn-ai-assistant]');
  if (!(button instanceof HTMLElement)) return;

  event.preventDefault();
  if (button.getAttribute('aria-busy') === 'true') return;

  // The floating shopping assistant (snippets/vx-assistant.liquid) is the store's chat: open it.
  const floatingAssistant = document.querySelector('[data-vx-ai-launch]');
  if (floatingAssistant instanceof HTMLElement) {
    if (floatingAssistant.getAttribute('aria-expanded') !== 'true') floatingAssistant.click();
    return;
  }

  button.setAttribute('aria-busy', 'true');

  if (document.querySelector('vn-ai-chat')) {
    const aiChat = await getAiChat();
    button.removeAttribute('aria-busy');
    if (aiChat) aiChat.open();
    else showStatus(button, 'Chat is not available right now. Please try again in a moment.');
    return;
  }

  const ready = await whenChatReady();
  button.removeAttribute('aria-busy');

  if (!ready || !openChat()) {
    showStatus(button, 'Chat is not available right now. Please try again in a moment.');
  }
});

customElements.whenDefined('shopify-chat').then(() => {
  document.querySelectorAll('[data-vn-ai-assistant]').forEach((button) => {
    if (button instanceof HTMLElement) button.dataset.vnChat = 'ready';
  });
});
